import { useCallback, useEffect, useState } from "react";
import { Mic, Square, Send, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { queueRecording, isNetworkError, recordingForm } from "@/lib/outbox";
import { PendingRecordings } from "@/components/recording/PendingRecordings";
import { useLang } from "@/context/LangContext";
import { useRecorder, fmt, MAX_SECONDS } from "@/components/recording/useRecorder";
import { MyRecordings } from "@/components/recording/MyRecordings";

const pill = "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full transition-colors disabled:opacity-50";

export const RecordPanel = ({ bookId, page, onStart }) => {
  const { t } = useLang();
  const r = t.rec;
  const rec = useRecorder();
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/my/recordings", { params: { book_id: bookId } })
      .then((res) => setItems(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, [bookId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { rec.reset(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => { onStart?.(); rec.start(); };

  const send = async () => {
    if (!rec.clip) return;
    setSending(true);
    const clip = { blob: rec.clip.blob, bookId, page, duration: rec.clip.duration };
    try {
      await api.post("/recordings", recordingForm(clip));
      toast.success(r.sent);
      rec.reset();
      load();
    } catch (e) {
      if (isNetworkError(e) && (await queueRecording(clip).then(() => true).catch(() => false))) {
        toast.info(r.queued);
        rec.reset();
      } else toast.error(r.sendError);
    } finally { setSending(false); }
  };

  return (
    <div className="px-4 sm:px-6 py-3 bg-[#047857]/10 border-b border-black/10 text-sm space-y-3" data-testid="record-panel">
      <p className="opacity-80">{r.hint} <span className="text-xs opacity-80">({r.max})</span></p>
      <div className="flex flex-wrap items-center gap-2">
        {rec.state !== "recording" && !rec.clip && (
          <button onClick={start} data-testid="record-start-btn" className={`${pill} bg-[#B91C1C] text-white hover:bg-[#991B1B]`}><Mic className="w-4 h-4" /> {r.start}</button>
        )}
        {rec.state === "recording" && (
          <>
            <button onClick={rec.stop} data-testid="record-stop-btn" className={`${pill} bg-[#1C1917] text-[#FAF7F2]`}><Square className="w-4 h-4 fill-current" /> {r.stop}</button>
            <span className="inline-flex items-center gap-2 font-semibold" data-testid="record-timer">
              <span className="w-2.5 h-2.5 rounded-full bg-[#B91C1C] animate-pulse" /> {r.recording} {fmt(rec.seconds)} / {fmt(MAX_SECONDS)}
            </span>
          </>
        )}
        {rec.clip && (
          <>
            <audio src={rec.clip.url} controls className="h-9 max-w-full" data-testid="record-preview-audio" />
            <button onClick={send} disabled={sending} data-testid="record-send-btn" className={`${pill} bg-[#047857] text-white hover:bg-[#065F46]`}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? r.sending : r.send}
            </button>
            <button onClick={rec.reset} disabled={sending} data-testid="record-retry-btn" className={`${pill} border border-current hover:bg-black/10`}><RotateCcw className="w-4 h-4" /> {r.retry}</button>
          </>
        )}
      </div>
      {rec.state === "error" && <p className="text-[#B91C1C] text-xs font-semibold" data-testid="record-mic-error">{r.micError}</p>}
      <PendingRecordings bookId={bookId} page={page} onSynced={load} />
      <MyRecordings items={items.filter((x) => x.page === page)} />
    </div>
  );
};
